'use client'
import * as React from 'react'
import { TextSelection } from '@tiptap/pm/state'
import { normalizeSelectionToTextPosition } from '@tiptap-ui/lib/selection'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

// --- Icons ---
import { ListIcon } from '@tiptap-ui/components/tiptap-icons/list-icon'
import { ListOrderedIcon } from '@tiptap-ui/components/tiptap-icons/list-ordered-icon'

// --- Lib ---
import { findNodePosition, isNodeInSchema, isNodeTypeSelected, isValidPosition } from '@tiptap-ui/lib/tiptap-utils'

export const listIcons = {
  bulletList: ListIcon,
  orderedList: ListOrderedIcon,
}

export const listLabels = {
  bulletList: 'Bullet List',
  orderedList: 'Ordered List',
}

export const LIST_SHORTCUT_KEYS = {
  bulletList: 'mod+shift+8',
  orderedList: 'mod+shift+7',
}

/**
 * Checks if a list can be toggled in the current editor state
 */
export function canToggleList (editor, type, turnInto = true) {
  if (!editor || !editor.isEditable) return false
  if (!isNodeInSchema(type, editor) || isNodeTypeSelected(editor, ['image'])) { return false }

  if (!turnInto) {
    switch (type) {
      case 'bulletList':
        return editor.can().toggleBulletList()
      case 'orderedList':
        return editor.can().toggleOrderedList()
      default:
        return false
    }
  }

  try {
    const view = editor.view
    const state = view.state
    const selection = state.selection

    if (selection.empty || selection instanceof TextSelection) {
      const pos = findNodePosition({
        editor,
        node: state.selection.$anchor.node(1),
      })?.pos
      if (!isValidPosition(pos)) return false
    }

    return true
  } catch {
    return false
  }
}

/**
 * Checks if list is currently active
 */
export function isListActive (editor, type) {
  if (!editor || !editor.isEditable) return false

  switch (type) {
    case 'bulletList':
      return editor.isActive('bulletList')
    case 'orderedList':
      return editor.isActive('orderedList')
    default:
      return false
  }
}

/**
 * Toggles list in the editor
 */
export function toggleList (editor, type) {
  if (!editor || !editor.isEditable) return false
  if (!canToggleList(editor, type)) return false

  try {
    const view = editor.view
    normalizeSelectionToTextPosition(view)
    const chain = editor.chain().focus()

    if (editor.isActive(type)) {
      // Unwrap list
      chain
        .liftListItem('listItem')
        .lift('bulletList')
        .lift('orderedList')
        .run()
    } else {
      // Wrap in specific list type
      const toggleMap = {
        bulletList: () => chain.toggleBulletList(),
        orderedList: () => chain.toggleOrderedList(),
      }

      const toggle = toggleMap[type]
      if (!toggle) return false

      toggle().run()
    }

    // Keep cursor within the affected block; avoid jumping to the
    // bottom-right cell by relying on current selection after toggle.
    editor.chain().focus().run()

    return true
  } catch {
    return false
  }
}

/**
 * Determines if the list button should be shown
 */
export function shouldShowButton (props) {
  const { editor, type, hideWhenUnavailable } = props

  if (!editor || !editor.isEditable) return false
  if (!isNodeInSchema(type, editor)) return false

  if (hideWhenUnavailable && !editor.isActive('code')) {
    return canToggleList(editor, type)
  }

  return true
}

/**
 * Custom hook that provides list functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MySimpleListButton() {
 *   const { isVisible, handleToggle, isActive } = useList({ type: "bulletList" })
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleToggle}>Bullet List</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedListButton() {
 *   const { isVisible, handleToggle, label, isActive } = useList({
 *     type: "orderedList",
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onToggled: () => console.log('List toggled!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleToggle}
 *       aria-label={label}
 *       aria-pressed={isActive}
 *     >
 *       Toggle List
 *     </MyButton>
 *   )
 * }
 * ```
 */
export function useList (config) {
  const {
    editor: providedEditor,
    type,
    hideWhenUnavailable = false,
    onToggled,
  } = config

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = React.useState(true)
  const canToggle = canToggleList(editor, type)
  const isActive = isListActive(editor, type)

  React.useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setIsVisible(shouldShowButton({ editor, type, hideWhenUnavailable }))
    }

    handleSelectionUpdate()

    editor.on('selectionUpdate', handleSelectionUpdate)

    return () => {
      editor.off('selectionUpdate', handleSelectionUpdate)
    }
  }, [editor, type, hideWhenUnavailable])

  const handleToggle = React.useCallback(() => {
    if (!editor) return false

    const success = toggleList(editor, type)
    if (success) {
      onToggled?.()
    }
    return success
  }, [editor, type, onToggled])

  return {
    isVisible,
    isActive,
    handleToggle,
    canToggle,
    label: listLabels[type],
    shortcutKeys: LIST_SHORTCUT_KEYS[type],
    Icon: listIcons[type],
  }
}
